import { CSSProperties } from 'react'

import { CSSObject, Theme } from '@emotion/react'

type Spacing = keyof Theme['ui']['spacings']
type Color = CSSObject['color'] | keyof Theme['ui']['colors']
type FontFamily =
  | keyof Theme['ui']['typography']['fontFamilies']
  | CSSObject['fontWeight']
type Size = keyof Theme['ui']['spacings'] | CSSObject['width']
type FontSize =
  | keyof Theme['ui']['typography']['fontSizes']
  | CSSObject['fontSize']
type LineHeight =
  | keyof Theme['ui']['typography']['lineHeights']
  | CSSObject['lineHeight']
type FontWeight =
  | keyof Theme['ui']['typography']['fontWeights']
  | CSSObject['fontWeight']

export interface StyleProps {
  /** margin */
  margin?: Spacing
  /** margin y-axis (up and down) */
  my?: Spacing
  /** margin x-axis (left and right) */
  mx?: Spacing
  /** margin-left */
  ml?: Spacing
  /** margin-right */
  mr?: Spacing
  /** margin-top */
  mt?: Spacing
  /** margin-bottom */
  mb?: Spacing
  /** margin-inline-start */
  mis?: Spacing
  /** margin-inline-end */
  mie?: Spacing

  /** padding */
  padding?: Spacing
  /** padding-left */
  pl?: Spacing
  /** padding-right */
  pr?: Spacing
  /** padding-top */
  pt?: Spacing
  /** padding-bottom */
  pb?: Spacing
  /** padding y-axis (up and down) */
  py?: Spacing
  /** padding x-axis (left and right) */
  px?: Spacing
  /** padding-inline-start */
  pis?: Spacing
  /** padding-inline-end */
  pie?: Spacing

  /** border */
  bd?: CSSObject['border'] // cannot be `border` because of HTML attributes overlap caused by emotion prop forwarding
  /** border-top */
  bt?: CSSObject['borderTop']
  /** border-bottom */
  bb?: CSSObject['borderBottom']
  /** border-left */
  bl?: CSSObject['borderLeft']
  /** border-right */
  br?: CSSObject['borderRight']
  /** border-color */
  bc?: Color
  /** border-width */
  bw?: Spacing
  /** border-style */
  bs?: CSSObject['borderStyle']
  /** border-radius */
  bra?: Spacing

  /** color  */
  color?: Color
  /** opacity */
  opacity?: CSSObject['opacity']

  /** font-family */
  ff?: FontFamily
  /** font-size */
  fz?: FontSize
  /** font-weight */
  fw?: FontWeight
  /** letter-spacing */
  lts?: CSSObject['letterSpacing']
  /** text-align */
  ta?: CSSObject['textAlign']
  /** line-height */
  lh?: LineHeight
  /** font-style */
  fs?: CSSObject['fontStyle']
  /** text-transform */
  tt?: CSSObject['textTransform']
  /** text-decoration */
  td?: CSSObject['textDecoration']
  /** white-space */
  ws?: CSSObject['whiteSpace']
  /** text-overflow */
  tow?: CSSObject['textOverflow']
  /** word-break */
  wb?: CSSObject['wordBreak']
  /** text-wrap */
  tw?: CSSObject['textWrap']

  /** width */
  w?: Size // cannot be `width` because of HTML attributes overlap caused by emotion prop forwarding
  /** min-width */
  miw?: Size
  /** max-width */
  maw?: Size
  /** height */
  h?: Size // cannot be `height` because of HTML attributes overlap caused by emotion prop forwarding
  /** min-height */
  mih?: Size
  /** max-height */
  mah?: Size
  /** overflow */
  overflow?: CSSObject['overflow']
  /** overflow-x */
  overflowX?: CSSObject['overflowX']
  /** overflow-y */
  overflowY?: CSSObject['overflowY']

  /** background */
  background?: Color
  /** background-size */
  bgsz?: Size
  /** background-position */
  bgp?: CSSObject['backgroundPosition']
  /** background-repeat */
  bgr?: CSSObject['backgroundRepeat']
  /** background-attachment */
  bga?: CSSObject['backgroundAttachment']
  /** background-color */
  bgc?: Color

  // position
  position?: CSSObject['position']
  top?: Size
  left?: Size
  bottom?: Size
  right?: Size
  inset?: Size
  /** z-index */
  zi?: CSSObject['zIndex']

  cursor?: CSSObject['cursor']
  display?: CSSObject['display']
  /** vertical-align */
  va?: CSSObject['verticalAlign']

  // flexbox props
  flex?: CSSObject['flex']
  /** flex-direction */
  fd?: CSSObject['flexDirection']
  /** align-items */
  ai?: CSSObject['alignItems']
  /** justify-content */
  jc?: CSSObject['justifyContent']
  /** justify-items */
  ji?: CSSObject['justifyItems']
  /** gap */
  gap?: Spacing
  /** flex-wrap */
  wrap?: CSSObject['flexWrap']
  /** flex-shrink */
  fsh?: CSSObject['flexShrink']
  /** flex-grow */
  fg?: CSSObject['flexGrow']
  /** align-self */
  ase?: CSSObject['alignSelf']
  /** align-content */
  ac?: CSSObject['alignContent']

  /** list-style */
  ls?: CSSObject['listStyle']
}

const StyleValuesMapping = {
  spacing: (theme: Theme, val?: Spacing) => {
    if (!val) {
      return null
    }

    if (typeof val === 'string' && theme.ui.spacings.hasOwnProperty(val)) {
      return theme.ui.spacings[val as keyof Theme['ui']['spacings']]
    }

    return val
  },
  identity: (theme: Theme, val: any) => val,
  color: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (typeof val === 'string' && theme.ui.colors.hasOwnProperty(val)) {
      return theme.ui.colors[val as keyof Theme['ui']['colors']]
    }

    return val
  },
  fontFamily: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (
      typeof val === 'string' &&
      theme.ui.typography.fontFamilies.hasOwnProperty(val)
    ) {
      return theme.ui.typography.fontFamilies[
        val as keyof Theme['ui']['typography']['fontFamilies']
      ]
    }

    return val
  },
  fontSize: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (
      typeof val === 'string' &&
      theme.ui.typography.fontSizes.hasOwnProperty(val)
    ) {
      return theme.ui.typography.fontSizes[
        val as keyof Theme['ui']['typography']['fontSizes']
      ]
    }

    return val
  },
  fontWeight: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (
      typeof val === 'string' &&
      theme.ui.typography.fontWeights.hasOwnProperty(val)
    ) {
      return theme.ui.typography.fontWeights[
        val as keyof Theme['ui']['typography']['fontWeights']
      ]
    }

    return val
  },
  lineHeight: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (
      typeof val === 'string' &&
      theme.ui.typography.lineHeights.hasOwnProperty(val)
    ) {
      return theme.ui.typography.lineHeights[
        val as keyof Theme['ui']['typography']['lineHeights']
      ]
    }

    return val
  },
  size: (theme: Theme, val: any) => {
    if (!val) {
      return null
    }

    if (typeof val === 'string' && theme.ui.spacings.hasOwnProperty(val)) {
      return theme.ui.spacings[val as keyof Theme['ui']['spacings']]
    }

    return val
  },
} as const

export interface SystemPropData {
  type: keyof typeof StyleValuesMapping
  property: keyof CSSProperties
}

export const STYLE_PROPS_DATA: Record<keyof StyleProps, SystemPropData> = {
  margin: { type: 'spacing', property: 'margin' },
  mt: { type: 'spacing', property: 'marginTop' },
  mb: { type: 'spacing', property: 'marginBottom' },
  ml: { type: 'spacing', property: 'marginLeft' },
  mr: { type: 'spacing', property: 'marginRight' },
  mis: { type: 'spacing', property: 'marginInlineStart' },
  mie: { type: 'spacing', property: 'marginInlineEnd' },
  mx: { type: 'spacing', property: 'marginInline' },
  my: { type: 'spacing', property: 'marginBlock' },

  padding: { type: 'spacing', property: 'padding' },
  pt: { type: 'spacing', property: 'paddingTop' },
  pb: { type: 'spacing', property: 'paddingBottom' },
  pl: { type: 'spacing', property: 'paddingLeft' },
  pr: { type: 'spacing', property: 'paddingRight' },
  pis: { type: 'spacing', property: 'paddingInlineStart' },
  pie: { type: 'spacing', property: 'paddingInlineEnd' },
  px: { type: 'spacing', property: 'paddingInline' },
  py: { type: 'spacing', property: 'paddingBlock' },

  bd: { type: 'identity', property: 'border' },
  bt: { type: 'identity', property: 'borderTop' },
  bb: { type: 'identity', property: 'borderBottom' },
  bl: { type: 'identity', property: 'borderLeft' },
  br: { type: 'identity', property: 'borderRight' },
  bc: { type: 'color', property: 'borderColor' },
  bw: { type: 'spacing', property: 'borderWidth' },
  bs: { type: 'identity', property: 'borderStyle' },
  bra: { type: 'spacing', property: 'borderRadius' },
  color: { type: 'color', property: 'color' },
  opacity: { type: 'identity', property: 'opacity' },

  ff: { type: 'fontFamily', property: 'fontFamily' },
  fz: { type: 'fontSize', property: 'fontSize' },
  fw: { type: 'fontWeight', property: 'fontWeight' },
  lts: { type: 'size', property: 'letterSpacing' },
  ta: { type: 'identity', property: 'textAlign' },
  lh: { type: 'lineHeight', property: 'lineHeight' },
  fs: { type: 'identity', property: 'fontStyle' },
  tt: { type: 'identity', property: 'textTransform' },
  td: { type: 'identity', property: 'textDecoration' },
  ws: { type: 'identity', property: 'whiteSpace' },
  tow: { type: 'identity', property: 'textOverflow' },
  wb: { type: 'identity', property: 'wordBreak' },
  tw: { type: 'identity', property: 'textWrap' },

  w: { type: 'size', property: 'width' },
  miw: { type: 'size', property: 'minWidth' },
  maw: { type: 'size', property: 'maxWidth' },
  h: { type: 'size', property: 'height' },
  mih: { type: 'size', property: 'minHeight' },
  mah: { type: 'size', property: 'maxHeight' },
  overflow: { type: 'identity', property: 'overflow' },
  overflowX: { type: 'identity', property: 'overflowX' },
  overflowY: { type: 'identity', property: 'overflowY' },

  background: { type: 'color', property: 'background' },
  bgsz: { type: 'size', property: 'backgroundSize' },
  bgp: { type: 'identity', property: 'backgroundPosition' },
  bgr: { type: 'identity', property: 'backgroundRepeat' },
  bga: { type: 'identity', property: 'backgroundAttachment' },
  bgc: { type: 'color', property: 'backgroundColor' },

  position: { type: 'identity', property: 'position' },
  top: { type: 'size', property: 'top' },
  left: { type: 'size', property: 'left' },
  bottom: { type: 'size', property: 'bottom' },
  right: { type: 'size', property: 'right' },
  inset: { type: 'size', property: 'inset' },
  zi: { type: 'identity', property: 'zIndex' },

  cursor: { type: 'identity', property: 'cursor' },
  display: { type: 'identity', property: 'display' },
  va: { type: 'identity', property: 'verticalAlign' },

  flex: { type: 'identity', property: 'flex' },
  fd: { type: 'identity', property: 'flexDirection' },
  ai: { type: 'identity', property: 'alignItems' },
  jc: { type: 'identity', property: 'justifyContent' },
  ji: { type: 'identity', property: 'justifyItems' },
  gap: { type: 'size', property: 'gap' },
  wrap: { type: 'identity', property: 'flexWrap' },
  fsh: { type: 'identity', property: 'flexShrink' },
  fg: { type: 'identity', property: 'flexGrow' },
  ase: { type: 'identity', property: 'alignSelf' },
  ac: { type: 'identity', property: 'alignContent' },
  ls: { type: 'identity', property: 'listStyle' },
} as const

export const getCSSObjectFromStyleProps = ({
  theme,
  ...props
}: StyleProps & { theme: Theme }) => {
  const result: Record<keyof CSSObject, CSSObject[number]> = {}
  for (const prop in props) {
    if (STYLE_PROPS_DATA.hasOwnProperty(prop)) {
      const propName = prop as keyof StyleProps
      // we can do typecast because TS is not clever to consider above if statement
      const { type, property } = STYLE_PROPS_DATA[propName]
      result[property] = StyleValuesMapping[type](theme, props[propName])
    }
  }

  return result
}
